import { Banner, Button, Dialog, Field, Input, Select } from '@cloudflare/kumo';
import { useState, useEffect } from 'react';
import { confirmationMatches, DELETE_WORD } from './DangerConfirmModal';
import { DangerButton } from './ui/DangerButton';
import { useCategories } from '../hooks/useCategories';
import { api } from '../services/api';
import { useAppToast } from '../hooks/useAppToast';

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedContactIds: string[];
  onSuccess: () => void;
}

/** O `Select` do Cloudscape não aceita valor vazio: "sem categoria" vira esta sentinela. */
const NO_CATEGORY = '__none__';

const ACTION_OPTIONS = [
  { value: 'update', label: 'Atualizar Categoria' },
  { value: 'delete', label: 'Excluir Contatos' },
];

export function BulkEditModal({ isOpen, onClose, selectedContactIds, onSuccess }: BulkEditModalProps) {
  const toast = useAppToast();
  const [categoriaId, setCategoriaId] = useState<string>('');
  const [action, setAction] = useState<'update' | 'delete'>('update');
  const [typed, setTyped] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { categories } = useCategories();

  useEffect(() => {
    if (isOpen) {
      setCategoriaId('');
      setAction('update');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedContactIds.length === 0) {
      toast.error('Nenhum contato selecionado');
      return;
    }

    if (action === 'update' && !categoriaId) {
      toast.error('Selecione uma categoria');
      return;
    }

    setIsSubmitting(true);
    try {
      if (action === 'update') {
        await api.post('/contatos/bulk/update', {
          contactIds: selectedContactIds,
          updates: { categoriaId: categoriaId || null },
        });
        toast.success(`${selectedContactIds.length} contato(s) atualizado(s) com sucesso!`);
      } else {
        if (!confirmationMatches(typed)) { toast.error(`Digite ${DELETE_WORD} para confirmar a exclusão`); setIsSubmitting(false); return; }
        await api.post('/contatos/bulk/delete', { contactIds: selectedContactIds });
        toast.success(`${selectedContactIds.length} contato(s) excluído(s) com sucesso!`);
      }
      onSuccess();
    } catch (error: unknown) {
      const apiErr = error as { response?: { data?: { error?: string } } };
      toast.error(apiErr?.response?.data?.error || 'Erro ao processar operação em massa');
    } finally {
      setIsSubmitting(false);
    }
  };

  const categoryOptions = [
    { value: NO_CATEGORY, label: 'Sem categoria' },
    ...categories.map((cat) => ({ value: cat.id, label: cat.nome })),
  ];

  return (
    <Dialog.Root open={isOpen} onOpenChange={(o: boolean) => { if (!o) (onClose)(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Edição em Massa</h3>}</Dialog.Title>

      <p className="!text-[var(--text-muted)] mb-6">
        {selectedContactIds.length} contato(s) selecionado(s)
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Field label="Ação">
          <Select value={(ACTION_OPTIONS.find((o) => o.value === action) ?? null)?.value ?? null} items={ACTION_OPTIONS} onValueChange={(value: string | null) => setAction((value ?? 'update') as 'update' | 'delete')} />
        </Field>

        {action === 'update' && (
          <Field label="Categoria">
            <Select value={(categoryOptions.find((o) => o.value === (categoriaId || NO_CATEGORY)) ?? null)?.value ?? null} items={categoryOptions} onValueChange={(value: string | null) => setCategoriaId(value === NO_CATEGORY ? '' : value ?? '')} placeholder="Selecione uma categoria" />
          </Field>
        )}

        {action === 'delete' && (
          <>
            <Banner variant="error" description={<>
              Atenção: esta ação exclui permanentemente {selectedContactIds.length} contato(s) e não pode ser desfeita.
            </>} />
            <Field label={`Digite ${DELETE_WORD} para confirmar`}>
              <Input onChange={(e) => setTyped(e.target.value)} aria-label={`Digite ${DELETE_WORD} para confirmar`} value={typed} placeholder={DELETE_WORD} autoComplete="off" />
            </Field>
          </>
        )}

        <div className="flex gap-3 pt-2 justify-end">
          <Button variant="secondary" type="button" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          {action === 'delete' ? (
            <DangerButton
              loading={isSubmitting}
              disabled={isSubmitting || !confirmationMatches(typed)}
            >
              Excluir
            </DangerButton>
          ) : (
            <Button type="submit" variant="primary" loading={isSubmitting} disabled={isSubmitting}>
              Atualizar
            </Button>
          )}
        </div>
      </form>
    
</Dialog>
</Dialog.Root>
  );
}
